import PropTypes from 'prop-types';
import { createContext, useEffect, useState, useMemo, useCallback } from 'react';
// hooks
import useOptions from '../hooks/useOptions';
import useApiUrl from '../hooks/useApiUrl';

const axios = require('axios').default;

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  method: 'auth0',
  login: () => {},
  logout: () => {}
};

const AuthContext = createContext(initialState);

AuthProvider.propTypes = {
  children: PropTypes.node
};

// TODO: stale closures — `authLogin`, `logout`, `requestResetPassword` et
// `resetPassword` ne sont pas mémorisés avec `useCallback` et ne figurent pas
// dans les deps du `useMemo` du contextValue. Ils capturent donc `userInfo` /
// `options` au moment où ils sont créés. Plusieurs appels à `setUserInfo({ ...userInfo, ... })`
// et `setOptions({ ...options, ... })` utilisent encore cette forme non
// fonctionnelle — si deux updates se chevauchent (requête lente + action user),
// la photocopie écrase les données fraîches.
//
// Pas bloquant aujourd'hui (les scénarios de race sont rares), mais à corriger
// un jour :
//   1. Envelopper chaque handler dans `useCallback` avec les bonnes deps
//   2. Remplacer tous les `setUserInfo({ ...userInfo, ... })` par la forme
//      fonctionnelle `setUserInfo((prev) => ({ ...prev, ... }))` (idem setOptions)
//   3. Ajouter les handlers aux deps du `useMemo` du contextValue
// `useEffect` d'init (ligne ~31) utilise déjà la bonne forme, c'est le modèle à suivre.
function AuthProvider({ children }) {
  const { manounaApiUrl, appApiUrl } = useApiUrl();
  const [userInfo, setUserInfo] = useState(initialState);
  const [options, setOptions] = useOptions();

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const initialize = async () => {
      try {
        const sessionToken = localStorage.getItem('sessionToken');

        if (!sessionToken) {
          if (isMounted) {
            setUserInfo((prev) => ({
              ...prev,
              isInitialized: true,
              isAuthenticated: false
            }));
          }
          return;
        }

        axios.defaults.headers.common.Authorization = sessionToken;

        // If the user has not a valid token
        // An error is thrown and catched below
        const info = await axios.get(`${manounaApiUrl}checkSession`, {
          signal: controller.signal
        });
        const { name, email, mutuelles, tarifs, profileComplete } = info.data;

        if (isMounted) {
          setOptions((prev) => ({
            ...prev,
            mutuelles,
            tarifs
          }));

          setUserInfo((prev) => ({
            ...prev,
            name,
            email,
            profileComplete,
            isInitialized: true,
            isAuthenticated: true
          }));
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }

        localStorage.removeItem('sessionToken');
        delete axios.defaults.headers.common.Authorization;

        if (isMounted) {
          setUserInfo((prev) => ({
            ...prev,
            isInitialized: true,
            isAuthenticated: false
          }));
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const authLogin = (username, password) =>
    new Promise((resolver) => {
      axios
        .post(`${appApiUrl}login`, { username, password })
        .then((response) => {
          const {
            name,
            email,
            sessionToken,
            mutuelles,
            tarifs,
            profileComplete
          } = response.data;

          setOptions({
            ...options,
            mutuelles,
            tarifs
          });

          axios.defaults.headers.common.Authorization = sessionToken;
          localStorage.setItem('sessionToken', sessionToken);
          setUserInfo({
            ...userInfo,
            name,
            email,
            profileComplete,
            isAuthenticated: true
          });
          resolver(response.data);
        })
        .catch(() => {
          resolver(null);
        });
    });

  const logout = () => {
    localStorage.removeItem('sessionToken');
    delete axios.defaults.headers.common.Authorization;
    setUserInfo({ ...userInfo, isAuthenticated: false });
  };

  const requestResetPassword = (email) =>
    new Promise((resolver) => {
      axios
        .get(`${appApiUrl}RequestResetPassword/${email}`)
        .then(() => {
          resolver(true);
        })
        .catch((err) => {
          if (err.response.status === 404) {
            alert('Aucun utilisateur trouvé correspondant à cet mail');
          }

          resolver(false);
        });
    });

  const resetPassword = (token, newPassword) =>
    new Promise((resolver) => {
      axios
        .get(`${appApiUrl}resetpassword/${token}/${newPassword}`)
        .then((response) => {
          const {
            name,
            email,
            sessionToken,
            mutuelles,
            tarifs,
            profileComplete
          } = response.data;

          setOptions({
            ...options,
            mutuelles,
            tarifs
          });

          axios.defaults.headers.common.Authorization = sessionToken;
          localStorage.setItem('sessionToken', sessionToken);
          setUserInfo({
            ...userInfo,
            name,
            email,
            profileComplete,
            isAuthenticated: true
          });
          resolver(response.data);
        })
        .catch((err) => {
          if (err.response.status === 404) {
            alert('Token invalide');
          }

          resolver(false);
        });
    });

  const setIsProfileComplete = useCallback(
    (newValue) =>
      setUserInfo((prev) => ({
        ...prev,
        profileComplete: newValue
      })),
    []
  );

  const contextValue = useMemo(
    () => ({
      ...userInfo,
      setIsProfileComplete,
      login: authLogin,
      requestResetPassword,
      resetPassword,
      logout
    }),
    [userInfo, setIsProfileComplete]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export { AuthProvider, AuthContext };
